import { useQueries } from '@tanstack/react-query';
import { onboardingApi } from '../api/onboardingApi';
import type { StorefrontStatus } from '../types';

/**
 * Fleet view: fetch every tenant's storefront standup status in parallel.
 *
 * Reuses the SAME react-query key as `useStorefrontStatus` (single-slug, on the
 * merchant detail page) so the cache is shared — opening a merchant after viewing
 * the readiness board is instant, and either view refreshes the other.
 */
const statusKey = (slug: string) => ['storefront-onboarding', slug, 'status'] as const;

export interface StorefrontStatusResult {
  slug: string;
  status: StorefrontStatus | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useStorefrontReadiness(slugs: string[]): {
  results: StorefrontStatusResult[];
  isLoading: boolean;
  isFetching: boolean;
} {
  const queries = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: statusKey(slug),
      queryFn: () => onboardingApi.getStatus(slug),
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
      enabled: !!slug,
    })),
  });

  const results: StorefrontStatusResult[] = slugs.map((slug, i) => ({
    slug,
    status: queries[i]?.data,
    isLoading: !!queries[i]?.isLoading,
    isError: !!queries[i]?.isError,
  }));

  return {
    results,
    isLoading: queries.some((q) => q.isLoading),
    isFetching: queries.some((q) => q.isFetching),
  };
}
